import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Fitter } from '@sc/data-access-models';
import { JsonListKey, StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { AuthService } from '@sc/feature-auth-okta';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';
import { FitterRouterService } from '@sc/feature-fitter-path-router-system';
import {
  DossierManagerService,
  FittersManagerService,
  User,
} from '@sc/feature-fitter-shared-libs';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  ButtonComponent,
  IconComponent,
  InsurerLogoComponent,
} from '@sc/ui-components';
import { combineLatest, map, mergeMap } from 'rxjs';
import { NotificationInfos } from '../../models';
import { NavBarSandboxService } from '../../services';
import { NotificationCenterBodyComponent } from '../notification-center-body/notification-center-body.component';

export interface NavBarUser {
  name: string;
  lastName: string;
}

@Component({
  selector: 'sc-fitter-navbar',
  styleUrls: ['./nav-bar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FitterNameDisplayService],
  imports: [
    IconComponent,
    InsurerLogoComponent,
    NgIf,
    ButtonComponent,
    NgClass,
    NotificationCenterBodyComponent,
    MatMenuModule,
    NgFor,
    AsyncPipe,
    TranslateModule,
  ],
  template: `
    <!-- mobile nav bar, hidden in desktop by CSS -->
    <div class="mobile-nav-bar">
      <div class="menu-burger" (click)="toggleMobileNavBar(true)">
        <sc-icon class="mobile-menu-toggle" name="menu"></sc-icon>
      </div>
      <sc-insurer-logo></sc-insurer-logo>
      <sc-icon
        class="user-icon"
        name="user"
        (click)="toggleMobileNavBar(false)"
      ></sc-icon>
    </div>

    <div class="fitter-navbar-container" [class.active]="isMobileNavActive">
      <div class="leftSection">
        <sc-insurer-logo></sc-insurer-logo>
        <nav
          class="menu"
          role="navigation"
          *ngIf="isMobileNavType"
          [attr.aria-label]="
            'GlobalApplicativeConstants.accessibility.labels.navigation'
              | translate
          "
        >
          <ul class="navigation-list">
            <li>
              <sc-button
                *ngIf="hasPlanningAccess()"
                [ariaRole]="isOnPlanningPage ? undefined : 'link'"
                [ngClass]="{ 'selected-page': isOnPlanningPage }"
                (click)="goToPlanning()"
                >{{
                  'FeatureFitterNavBar.NavBar.planning' | translate
                }}</sc-button
              >
            </li>
            <li>
              <sc-button
                *ngIf="hasSettingsAccess()"
                [disabled]="!(fitterManagerService?.fitters$ | async)?.length"
                [ariaRole]="isOnSettingsPage ? undefined : 'link'"
                [ngClass]="{
                  'selected-page': isOnSettingsPage,
                  'mobile-hidden': true
                }"
                (click)="goToSettings()"
                >{{
                  'FeatureFitterNavBar.NavBar.settings' | translate
                }}</sc-button
              >
            </li>
            <li>
              <sc-button
                *ngIf="hasSearchAccess()"
                [disabled]="!(fitterManagerService?.fitters$ | async)?.length"
                [ariaRole]="isOnSearchPage ? undefined : 'link'"
                [ngClass]="{
                  'selected-page': isOnSearchPage,
                  'mobile-hidden': true
                }"
                (click)="goToSearchDossiers()"
                >{{
                  'FeatureFitterNavBar.NavBar.searchPage' | translate
                }}</sc-button
              >
            </li>
            <li>
              <sc-button
                *ngIf="hasDashboardAccess()"
                [disabled]="!(fitterManagerService?.fitters$ | async)?.length"
                [ariaRole]="isOnDashboardPage ? undefined : 'link'"
                [ngClass]="{ 'selected-page': isOnDashboardPage }"
                (click)="goToDashboard()"
                >{{
                  'FeatureFitterNavBar.NavBar.dashboard' | translate
                }}</sc-button
              >
            </li>
          </ul>
        </nav>
        <div
          class="mobile-logout"
          *ngIf="isMobileNavType === false"
          (click)="logoutUser(); this.clickOnLogOut = true"
        >
          <sc-icon name="logout" id="user-logout-icon"></sc-icon>
          {{ 'FeatureFitterNavBar.NavBar.logout' | translate }}
        </div>
      </div>

      <span class="rightSection">
        <div
          class="notifications-center-container"
          [class.open]="notificationCenterOpen"
          #notificationsCenterContainer
        >
          <span
            class="notification-center-btn"
            (click)="onNotifCenterBtnClick()"
          >
            <sc-icon name="bell"></sc-icon>
            <span
              id="not-seen-notifications-circle"
              [class.all-seen]="hasSeenAllNotifications()"
            ></span>
          </span>

          <span class="notification-center-body-container">
            <sc-notification-center-body
              [notifications]="shownNotifications()"
              [isFullNotifList]="areAllNotifShown()"
              (markAsRead)="onMarkAsRead()"
              (chargeMoreNotifs)="onChargeMoreNotifs()"
            ></sc-notification-center-body>
          </span>
        </div>

        <span class="user_span">
          <sc-icon name="user"></sc-icon>
          <span class="user_infos">
            <div class="logout">
              <button
                [matMenuTriggerFor]="menu"
                class="user-infos-menu-panel"
                [title]="
                  'FeatureFitterNavBar.NavBar.welcome'
                    | translate: { username: displayUserName() }
                "
              >
                <span class="user-infos-menu-panel-name">
                  {{
                    'FeatureFitterNavBar.NavBar.welcome'
                      | translate: { username: displayUserName() }
                  }}</span
                >
                <sc-icon name="arrow_top" id="open-arrow" aria-hidden="true">
                </sc-icon>
              </button>
              <mat-menu #menu="matMenu" class="custom-navbar-mat-menu">
                <button
                  mat-menu-item
                  class="selected-user-info"
                  [title]="
                    'FeatureFitterNavBar.NavBar.welcome'
                      | translate: { username: displayUserName() }
                  "
                >
                  <sc-icon
                    name="user"
                    id="user-information-icon"
                    aria-hidden="true"
                  ></sc-icon>
                  <span class="user-infos-menu-panel-name"
                    >{{
                      'FeatureFitterNavBar.NavBar.welcome'
                        | translate: { username: displayUserName() }
                    }}
                  </span>
                  <sc-icon
                    name="arrow_bottom"
                    id="close-arrow"
                    aria-hidden="true"
                  ></sc-icon>
                </button>
                <button
                  mat-menu-item
                  class="option-hover-color"
                  (click)="onSelectionChange('preferences')"
                >
                  <sc-icon name="gear" id="preferences-icon"></sc-icon
                  >{{ 'FeatureFitterNavBar.NavBar.preferences' | translate }}
                </button>
                <button
                  mat-menu-item
                  class="option-hover-color"
                  (click)="onSelectionChange('site-plan')"
                >
                  {{ 'FeatureFitterNavBar.NavBar.sitePlan' | translate }}
                </button>
                <button
                  *ngFor="let loggedUser of proposeLogginUsers"
                  id="switch-user"
                  mat-menu-item
                  (click)="onSelectionChange('switch-user', loggedUser.login)"
                >
                  <sc-icon name="logout" id="user-logout-icon"></sc-icon>
                  {{
                    (loggedUser.connectedUser.name ?? '') +
                      ' ' +
                      (loggedUser.connectedUser.lastName ?? '')
                  }}
                </button>
                <button
                  (click)="onSelectionChange('user-logout')"
                  mat-menu-item
                  value="user-logout"
                  id="user-logout"
                >
                  <sc-icon name="logout" id="user-logout-icon"></sc-icon>
                  {{ 'FeatureFitterNavBar.NavBar.logout' | translate }}
                </button>
              </mat-menu>
            </div>
          </span>
        </span>
      </span>
    </div>
  `,
})
export class NavBarComponent implements OnInit, OnDestroy {
  @ViewChild('notificationsCenterContainer', { read: ElementRef })
  notifCenterElem: ElementRef<HTMLElement>;

  isOnPlanningPage = false;
  isOnSettingsPage = false;
  isOnSearchPage = false;
  isOnDashboardPage = false;
  isOnPreferencesPage = false;
  isMobileNavActive = false; // toggle for open and close mobile menu
  lastLoggedUsers: User[] = [];
  isMobileNavType = true;
  proposeLogginUsers = [];
  connectedUser: NavBarUser = {
    name: '',
    lastName: '',
  };
  logoutForm = new FormControl('user-information');

  fitters: Fitter[] = [];
  notificationCenterOpen = false;
  allNotifications: NotificationInfos[] = [];
  nbNotifShown = 3;

  clickOnLogOut = false;
  constructor(
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly authOktaService: AuthService,
    private readonly routerService: FitterRouterService,
    private readonly navbarSB: NavBarSandboxService,
    private readonly translator: TranslatorService,
    private readonly storage: StorageService,
    private readonly snackbarService: SnackbarService,
    private readonly dossierManagerService: DossierManagerService,
    public fitterManagerService: FittersManagerService,
    private readonly fitterDisplayService: FitterNameDisplayService
  ) {}

  @HostListener('window:beforeunload')
  unloadHandler() {
    if (this.storage.getItem('forcelogout')) {
      this.storage.removeItem('forcelogout');
      return true;
    }
    this.navbarSB.storeNotifications(this.allNotifications);
    this.fitterManagerService.unsubscribeAll();
    setTimeout(() => {
      // relancer les souscriptions (si toujours là) !!
      this.fitterManagerService.subscribeToCurrentFitters();
    }, 15000);
    if (this.clickOnLogOut) {
      return true;
    }
    return false;
  }

  ngOnInit() {
    this.isOnPlanningPage = this.router?.routerState?.snapshot?.url.startsWith(
      `/${FitterRouterService.planningPageRoute}`
    );

    this.isOnSettingsPage = this.router?.routerState?.snapshot?.url.startsWith(
      `/${FitterRouterService.settingsPageRoute}`
    );

    this.isOnSearchPage = this.router?.routerState?.snapshot?.url.startsWith(
      `/${FitterRouterService.searchDossiersPageRoute}`
    );

    this.isOnDashboardPage = this.router?.routerState?.snapshot?.url.startsWith(
      `/${FitterRouterService.dashboardPageRoute}`
    );
    this.isOnPreferencesPage =
      this.router?.routerState?.snapshot?.url.startsWith(
        `/${FitterRouterService.preferences}`
      );

    this.initUser();
    this.navbarSB.getFittersByManager().subscribe(
      (fitterList) => {
        this.fitters = fitterList;
        this.changeDetectorRef.detectChanges();
      },
      (error: HttpErrorResponse) => {
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          this.translator.getInstantTranslation(
            error?.error?.message ||
              'FeatureFitterNavBar.NavBar.errorFittersFetch'
          )
        );

        console.error(error);
      }
    );

    this.allNotifications.push(...this.navbarSB.getStoredNotifications());

    this.navbarSB
      .getFittersByManager()
      .pipe(
        mergeMap((fitters) =>
          this.navbarSB
            .subscribeToNewDossierRequestApmt()
            .pipe(map((newDossier) => ({ newDossier, fitters })))
        )
      )
      .subscribe(({ newDossier, fitters }) => {
        const foundFitter = fitters.find(
          (f) =>
            f.id ===
            (newDossier.dossier?.appointment?.fitterId ||
              newDossier.dossier?.requestAppointment?.fitterId)
        );

        const newNotification: NotificationInfos = {
          title: `FeatureFitterNavBar.NotificationCenter.notificationTypes.apmtRequest.title`,
          timestamp: null,
          timezone: null,
          fitterName: this.fitterDisplayService.getInstantFitterNameTranslation(
            foundFitter,
            'notifCenter'
          ),
          carBrand: newDossier.dossier?.claim?.car?.manufacturer,
          carMordel: newDossier.dossier?.claim?.car?.model,
          customerFirstName: newDossier.dossier?.customer?.firstname,
          customerLastName: newDossier.dossier?.customer?.lastname,
          seen: false,
          alert: newDossier.dossier?.requestAppointment
            ?.isReplacementVehicleSelected
            ? 'FeatureFitterNavBar.NotificationCenter.notificationTypes.apmtRequest.alertVehicleSelected'
            : null,
        };

        this.allNotifications.unshift(newNotification);
        this.changeDetectorRef.detectChanges();
      });

    this.navbarSB.subscribeToTypedDossierUpdates().subscribe((newUpdate) => {
      const newNotifications: NotificationInfos[] = [];

      const foundFitter = this.fitters.find(
        (f) =>
          f.id ===
          (newUpdate.dossier?.appointment?.fitterId ||
            newUpdate.dossier?.requestAppointment?.fitterId)
      );

      newUpdate.updateType?.forEach((updateType) => {
        newNotifications.push({
          title: `FeatureFitterNavBar.NotificationCenter.notificationTypes.${updateType}.title`,
          timestamp: newUpdate.dossier?.appointment?.time,
          timezone: newUpdate.dossier?.appointment?.timezone,
          fitterName: this.fitterDisplayService.getInstantFitterNameTranslation(
            foundFitter,
            'notifCenter'
          ),
          carBrand: newUpdate.dossier?.claim?.car?.manufacturer,
          carMordel: newUpdate.dossier?.claim?.car?.model,
          customerFirstName: newUpdate.dossier?.customer?.firstname,
          customerLastName: newUpdate.dossier?.customer?.lastname,
          seen: false,
          alert: null,
        });
      });

      this.allNotifications.unshift(...newNotifications);
      this.changeDetectorRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.navbarSB.storeNotifications(this.allNotifications);
  }

  initUser() {
    combineLatest([
      this.authOktaService.isAuthenticated$(),
      this.authOktaService.getUserLogin$(),
      this.authOktaService
        .getUserFirstName$()
        .pipe(map((firstName) => `${firstName}`)),
      this.authOktaService
        .getUserLastName$()
        .pipe(map((lastName) => `${lastName}`)),
    ]).subscribe({
      next: ([isAuth, login, firstname, lastname]) => {
        if (isAuth) {
          this.connectedUser.name = firstname;
          this.connectedUser.lastName = lastname;
          this.lastLoggedUsers = this.storage
            .getItem<JsonListKey, User>('lastLoggedUsers', localStorage, [])
            .filter((user) => user.login && user.login != login)
            .map((user) => {
              user.inUse = false;
              return user;
            });
          this.lastLoggedUsers.push({
            connectedUser: this.connectedUser,
            login: login,
            timestamp: new Date(),
            inUse: true,
          });
          this.storage.setItem(
            'lastLoggedUsers',
            this.lastLoggedUsers,
            localStorage,
            []
          );
          this.proposeLogginUsers = this.lastLoggedUsers.filter(
            (user) => !user.inUse
          );
          this.changeDetectorRef.detectChanges();
        }
      },
    });
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(targetElement) {
    const clickedInside =
      this.notifCenterElem.nativeElement.contains(targetElement);
    if (!clickedInside && this.notificationCenterOpen) {
      this.notificationCenterOpen = false;
    }
  }

  displayUserName() {
    return (
      (this.connectedUser.name ?? '') +
      ' ' +
      (this.connectedUser.lastName ?? '')
    );
  }

  goToPlanning() {
    this.routerService.goToPlanning();
  }

  goToSettings() {
    this.routerService.goToSettings();
  }

  goToSearchDossiers() {
    this.routerService.goToSearchDossiers();
  }

  goToDashboard() {
    this.routerService.goToDashboard();
  }

  hasSeenAllNotifications() {
    return this.allNotifications?.filter((notif) => !notif.seen).length === 0;
  }

  onNotifCenterBtnClick() {
    this.notificationCenterOpen = !this.notificationCenterOpen;
  }

  onMarkAsRead() {
    this.allNotifications.forEach((notif) => {
      notif.seen = true;
    });
  }

  onChargeMoreNotifs() {
    this.nbNotifShown += 3;
  }

  shownNotifications() {
    return this.allNotifications?.filter((_, i) => i < this.nbNotifShown);
  }

  areAllNotifShown() {
    return this.nbNotifShown >= this.allNotifications.length;
  }

  toggleMobileNavBar(isMobileNavType) {
    this.isMobileNavActive = !this.isMobileNavActive;
    this.isMobileNavType = isMobileNavType;
  }

  onSelectionChange($event, value = '') {
    if ($event === 'user-logout') {
      this.clickOnLogOut = true;
      this.logoutUser();
    } else if ($event?.startsWith('switch-user')) {
      this.lastLoggedUsers = this.lastLoggedUsers.map((user) => ({
        ...user,
        inUse: user.login === value,
      }));
      this.storage.setItem('lastLoggedUsers', this.lastLoggedUsers);
      this.clickOnLogOut = true;
      this.logoutUser();
    } else if ($event === 'preferences') {
      this.routerService.goToPreferences();
    } else if ($event === 'site-plan') {
      this.routerService.goToSitePlan();
    }
    this.logoutForm.patchValue('user-information');
  }

  logoutUser() {
    this.fitterManagerService.unsubscribeAll();
    this.dossierManagerService.onCloseDossier().subscribe(async () => {
      await this.authOktaService.logout();
      location.reload();
    });
  }

  hasPlanningAccess() {
    return this.navbarSB.hasPlanningAccess();
  }

  hasDashboardAccess() {
    return this.navbarSB.hasDashboardAccess();
  }

  hasSettingsAccess() {
    return this.navbarSB.hasSettingsAccess();
  }

  hasSearchAccess() {
    return this.navbarSB.hasSearchAccess();
  }
}
